/* ============================================================================
 * 后端 API 客户端
 *
 * 纪律：后端不可用时**如实降级并标注**，不静默假装成功。
 * V2 的教训（B-01）：界面写「CP-SAT 整数规划」而代码是贪心，
 * 汇报场上经不起追问。V3 由 /api/health 决定顶栏标什么。
 * ========================================================================== */

/* API 基址。
 * 本地开发用相对路径 `/api`，由 Vite 的 dev 代理转到 127.0.0.1:8188（同源，无 CORS）。
 * 部署后前端是独立静态站、与 API 不同域，须在**构建时**注入 VITE_API_BASE。
 *
 * ⚠ Vite 的环境变量是编译期内联的，不是运行时读取——改了必须重新触发一次构建，
 *   否则表现为「环境变量明明改了，页面还在打旧地址」。
 *
 * 这个值是**人在 Render 控制台手工填**的，所以按"怎么填都对"来归一化三件事：
 *   ① 去掉末尾斜杠 —— 否则拼出来是 //health
 *   ② 漏了协议头就补 https:// —— 只填 `caps-v3-api.onrender.com` 的话，
 *      fetch 会把它当成**相对路径**打到静态站自己身上，报 404 而不是 CORS，
 *      排查方向会被带偏（本地 localhost 开头的保留 http，不然本地调试连不上）
 *   ③ 末尾没有 /api 就补上 —— Render 控制台里复制到的是服务根地址
 *      https://xxx.onrender.com，不带 /api；漏了就全站每个请求都 404 */
function resolveBase(): string {
  const raw = import.meta.env.VITE_API_BASE?.trim();
  if (!raw) return '/api';
  let s = raw.replace(/\/+$/, '');
  if (!/^https?:\/\//i.test(s) && !s.startsWith('/')) {
    s = `${/^(localhost|127\.0\.0\.1)(:|$)/i.test(s) ? 'http' : 'https'}://${s}`;
  }
  return /\/api$/.test(s) ? s : `${s}/api`;
}
const BASE = resolveBase();

export type SolverKernel = 'cpsat' | 'demo' | 'unavailable';

export interface HealthInfo {
  status: 'ok' | 'degraded' | 'offline';
  kernel: SolverKernel;
  ortools_version?: string;
  error?: string;
}

/* ── 登录令牌（A4）───────────────────────────────────────────────────
 * 令牌在**这一处**挂到请求上，不由各页面自己拼 header——
 * 漏挂一处就是一个绕过身份的后门。 */
let TOKEN: string | null = null;
export function setAuthToken(t: string | null) { TOKEN = t; }

/** 401 回调，由 store/auth 注入，避免 lib 反向依赖 store。 */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) { onUnauthorized = fn; }

function authHeaders(extra?: HeadersInit): HeadersInit {
  return TOKEN ? { ...extra, Authorization: `Bearer ${TOKEN}` } : { ...extra };
}

async function get<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(BASE + path, { ...init, headers: authHeaders(init?.headers) });
  if (res.status === 401) {
    onUnauthorized?.();
    throw new Error(`未登录或登录已过期 @ ${path}`);
  }
  if (res.status === 403) {
    const body = await res.json().catch(() => null) as { detail?: string } | null;
    throw new Error(body?.detail ?? `无权执行该操作 @ ${path}`);
  }
  if (!res.ok) throw new Error(`${res.status} ${res.statusText} @ ${path}`);
  return res.json() as Promise<T>;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  return get<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/* ── 认证 ───────────────────────────────────────────────────────────── */

export interface AuthUser {
  username: string; display_name: string; role: string; can: string[];
}
export interface LoginResult {
  token: string; expires_in: number; user: AuthUser; secret_ephemeral?: boolean;
}

export async function login(username: string, password: string): Promise<LoginResult> {
  const res = await fetch(`${BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (res.status === 401) throw new Error('账号或口令不正确');
  if (!res.ok) throw new Error(`登录失败：${res.status} ${res.statusText}`);
  return res.json() as Promise<LoginResult>;
}

export const authMe = () => get<AuthUser>('/auth/me');

export interface AuthMeta {
  mode: string; token: string; password: string; ttl_hours: number;
  secret_ephemeral: boolean;
  roles: Record<string, string[]>; can_name: Record<string, string>;
  limits: string[];
}
export const authMeta = () => get<AuthMeta>('/auth/meta');

/** 后端自检。失败不抛错——前端要能在纯前端模式下继续工作。 */
export async function health(): Promise<HealthInfo> {
  try {
    const ctl = new AbortController();
    const t = setTimeout(() => ctl.abort(), 2500);
    const r = await get<HealthInfo>('/health', { signal: ctl.signal });
    clearTimeout(t);
    return r;
  } catch {
    return { status: 'offline', kernel: 'demo' };
  }
}

/* ── 订单 ───────────────────────────────────────────────────────────── */

export interface ApiOrder {
  order_no: string; customer: string; customer_name: string; tier: string;
  product_code: string; product_name: string; spec: string;
  quantity_ton: number; due_day: number; due: string;
  priority: number; price: number; is_urgent: boolean;
}

export const listOrders = () => get<ApiOrder[]>('/orders');
export const getOrder = (no: string) => get<ApiOrder>(`/orders/${encodeURIComponent(no)}`);

/** 一条校验违规。severity='warn' 表示提醒但不拦截。 */
export interface Violation { kind: string; detail: string; severity?: 'warn' }

export interface OrderEditResult { order: ApiOrder; warnings: Violation[] }

/** 行内编辑被服务端拒绝。把逐条违规带出来，界面才能说清「为什么不让改」。 */
export class EditRejected extends Error {
  constructor(readonly violations: Violation[], message: string) { super(message); }
}

/** 提交一次订单行内编辑（F-27 + B-08）。
 *  校验不过抛 EditRejected，调用方据此让单元格保持原值。 */
export async function editOrder(
  no: string, field: string, value: unknown,
): Promise<OrderEditResult> {
  const res = await fetch(`${BASE}/orders/${encodeURIComponent(no)}/edit`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ field, value }),
  });
  if (res.status === 401) { onUnauthorized?.(); throw new Error('未登录或登录已过期'); }
  if (res.status === 403) {
    const b = await res.json().catch(() => null) as { detail?: string } | null;
    throw new Error(b?.detail ?? '无权编辑订单');
  }
  if (res.status === 422) {
    const body = await res.json() as { detail?: { message?: string; violations?: Violation[] } };
    const vs = body.detail?.violations ?? [];
    throw new EditRejected(vs, vs.map((v) => v.detail).join('；') || (body.detail?.message ?? '改动被拒绝'));
  }
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json() as Promise<OrderEditResult>;
}

export interface OrderEditableMeta {
  editable: string[]; labels: Record<string, string>; rules: string[];
}
export const orderEditableMeta = () => get<OrderEditableMeta>('/orders/meta/editable');

/* ── 派单 ───────────────────────────────────────────────────────────── */

export interface ApiAssignment {
  order_no: string; customer: string; product_code: string;
  factory: string; factory_short: string;
  qty_ton: number; unit_margin: number; due_day: number; priority: number;
}

export interface DispatchResult {
  label: string;
  kernel: 'cpsat';
  status: 'OPTIMAL' | 'FEASIBLE' | 'INFEASIBLE' | string;
  solve_seconds: number;
  objective: number;
  assignments: ApiAssignment[];
  unmet: { order_no: string; qty_ton: number }[];
  kpi: {
    orders: number; assigned: number; total_qty_ton: number; demand_ton: number;
    fulfill_pct: number; avg_margin: number; factories_used: number; unmet_orders: number;
  };
  decision_log: { order: string; level: string; text: string }[];
}

export interface DispatchOptions {
  capacity_scale?: Record<string, number>;
  disabled?: string[];
  /** L1 产品定向硬约束开关 */
  direction_on?: boolean;
  /** L2 工艺兼容硬约束开关 */
  compatible_on?: boolean;
  allow_split?: boolean;
  profit_weight?: number;
  lambda_late?: number;
  lambda_split?: number;
  time_limit?: number;
}

export const runDispatch = (opts: DispatchOptions = {}) =>
  post<DispatchResult>('/dispatch/run', opts);

/** 求解进度 SSE（B-06 → 解锁 F-44/F-46）。
 *  onStage 收到阶段推送，resolve 时返回最终结果。 */
export function streamDispatch(
  onStage: (s: { stage: number; total: number; text: string }) => void,
): Promise<DispatchResult> {
  return new Promise((resolve, reject) => {
    const es = new EventSource(`${BASE}/dispatch/stream`);
    const timer = setTimeout(() => { es.close(); reject(new Error('求解超时')); }, 90_000);
    es.onmessage = (e) => {
      try {
        const d = JSON.parse(e.data);
        if (d.done) {
          clearTimeout(timer); es.close(); resolve(d.result as DispatchResult);
        } else {
          onStage(d);
        }
      } catch (err) { clearTimeout(timer); es.close(); reject(err); }
    };
    es.onerror = () => { clearTimeout(timer); es.close(); reject(new Error('求解连接中断')); };
  });
}

/* ── 工序排产 ───────────────────────────────────────────────────────── */

export interface ApiOperation {
  order_no: string; customer: string; product_code: string; seq: number;
  wc_type: string; machine_code: string; machine_name: string;
  start_hour: number; end_hour: number; duration: number;
  quantity: number; due: number; frozen: boolean;
}

export interface ScheduleResult {
  label: string;
  status: string;
  solve_seconds: number;
  objective: number;
  /* ── 解质量三件套 ────────────────────────────────────────────────
   * 加权拖期 FJSP 的下界极难收紧（实测 8s 时间隙 13%~91%），
   * 所以界面**必须**区分「已证明最优」与「当前最好解」，不得一律写成最优。
   * 同时：解在 3 秒左右即收敛，间隙大不代表解差——延长时限改善的是证明不是解。 */
  best_bound?: number;
  gap_pct?: number;
  proven_optimal?: boolean;
  time_limit?: number;
  kpi: {
    jobs: number; operations: number;
    makespan_hours: number; makespan_days: number;
    tardiness_hours: number; late_jobs: number; on_time_pct: number;
    bottleneck_load_pct: number; change_deviation_hours: number;
  };
  operations: ApiOperation[];
  decision_log: { category: string; title: string; detail: string }[];
}

export const runSchedule = (body: {
  factory: string;
  assignments: ApiAssignment[];
  weights?: Record<string, number>;
  frozen?: Record<string, { machine_code: string; start: number }>;
  change_ref?: Record<string, number>;
  time_limit?: number;
}) => post<ScheduleResult>('/schedule/run', body);

/* ── 增量求解（B-07）─────────────────────────────────────────────────
 * 波动发生后不推倒重排：冻结在产与冻结窗口内的工序，上一版开工时刻进目标函数，
 * 改动权重调高、时限压到 5 秒。要的是"快且稳"，不是"重新最优"。 */

export interface DiffField { field: string; from: string | number; to: string | number; delta?: number }
export interface DiffCell {
  key: string; order_no: string; wc_type: string;
  change: 'modified' | 'added' | 'removed';
  fields: DiffField[]; frozen?: boolean;
}
export interface DiffSummary {
  baseline_ops: number; new_ops: number; unchanged: number;
  modified: number; added: number; removed: number;
  start_moved: number; machine_changed: number; churn_pct: number;
}
export interface OpsDiff { cells: DiffCell[]; summary: DiffSummary }

export interface IncrementalMeta {
  freeze_hours: number; frozen_ops: number; reference_ops: number; total_ops: number;
  change_weight: number; sla_seconds: number; wall_seconds: number;
  build_seconds: number; solver_budget: number;
  within_sla: boolean; boundary_note: string;
}

export interface IncrementalResult extends ScheduleResult {
  incremental: IncrementalMeta;
  diff: OpsDiff;
}

export const runIncremental = (body: {
  factory: string;
  assignments: ApiAssignment[];
  baseline_operations: ApiOperation[];
  freeze_hours?: number;
  change_weight?: number;
  time_limit?: number;
}) => post<IncrementalResult>('/schedule/incremental', body);

/* ── 情景树（B-09 / B-10）────────────────────────────────────────────── */

export interface ScenarioNode {
  id: string; parent_id: string | null; name: string;
  kind: 'main' | 'branch' | 'superseded';
  factory: string; note: string;
  params: Record<string, unknown>;
  kpi: ScheduleResult['kpi'];
  status: string; gap_pct: number | null; solve_seconds: number;
  merged_into: string | null; seq: number;
  created_at: string | null; created_by: string;
  operation_count: number;
  operations?: ApiOperation[];
}

export interface ScenarioDiff extends OpsDiff {
  left: ScenarioNode; right: ScenarioNode;
  kpi_diff: { key: string; left: number | null; right: number | null; delta: number | null }[];
}

export const listScenarios = () => get<ScenarioNode[]>('/scenario');
export const getScenario = (id: string) => get<ScenarioNode>(`/scenario/${encodeURIComponent(id)}`);
export const diffScenarios = (a: string, b: string) =>
  get<ScenarioDiff>(`/scenario/${encodeURIComponent(a)}/diff/${encodeURIComponent(b)}`);

export const createScenario = (body: {
  name: string; factory: string; assignments: ApiAssignment[];
  parent_id?: string | null; note?: string;
  weights?: Record<string, number>; time_limit?: number;
  actor?: string; kind?: 'main' | 'branch';
}) => post<ScenarioNode>('/scenario', body);

export const mergeScenario = (id: string, actor: string, note = '') =>
  post<{ new_main: ScenarioNode; superseded: ScenarioNode | null }>(
    `/scenario/${encodeURIComponent(id)}/merge`, { actor, note });

/* ── 拖拽约束校验（B-08 → 解锁 F-33/F-35）───────────────────────────── */

export interface ValidateResult {
  ok: boolean;
  violations: { kind: string; detail: string }[];
  suggestion: { start_hour: number; machine_code: string; reason: string } | null;
}

export const validateMove = (body: {
  factory: string;
  assignments: ApiAssignment[];
  moved: { order_no: string; wc_type: string; machine_code: string; start_hour: number };
  operations: ApiOperation[];
}) => post<ValidateResult>('/schedule/validate', body);

/* ── 排产审批状态机（B-12）─────────────────────────────────────────────
 * 两道硬闸的数字由**服务端自己算**，前端传什么都不作数。
 * V2 的闸门是前端 if 判断，改个变量就能绕过；这里绕不过。 */

export type ApprovalState =
  | 'DRAFT' | 'SUBMITTED' | 'VALIDATED' | 'APPROVED' | 'REJECTED'
  | 'RELEASED' | 'EXECUTING' | 'COMPLETED';

export type ApprovalAction =
  | 'submit' | 'withdraw' | 'validate' | 'approve' | 'reject'
  | 'release' | 'start' | 'complete';

export interface KittingLine {
  factory: string; material: string; material_name: string;
  gross_ton: number; on_hand: number; in_transit: number;
  net_ton: number; kitting_pct: number; buy_amount: number;
}

export interface GateDetail {
  consistency_pct?: number;
  consistency_meta?: { reason?: string; assigned?: number; covered?: number; by_factory?: Record<string, { run_id: number; jobs: number; status: string }> };
  kitting_pct?: number;
  kitting_short?: KittingLine[];
  gates?: Record<string, number>;
  fails?: string[];
  message?: string;
}

export interface ApprovalDto {
  id: string; plan_month: string;
  state: ApprovalState; state_name: string;
  consistency_pct: number | null; kitting_pct: number | null;
  updated_at: string | null;
  allowed: ApprovalAction[];
  step_index: number; step_total: number;
  gate_detail?: GateDetail;
}

export interface AuditDto {
  id: number; action: string; action_name: string; actor: string;
  before: string; after: string; at: string | null;
}

/** 动作被拒时抛出，携带服务端给出的闸门明细，界面据此显示"卡在哪一条" */
export class ApprovalRejected extends Error {
  constructor(readonly status: number, readonly detail: GateDetail | string) {
    super(typeof detail === 'string' ? detail : detail.message ?? '动作被拒绝');
    this.name = 'ApprovalRejected';
  }
}

export const getApproval = (id: string) => get<ApprovalDto>(`/approval/${encodeURIComponent(id)}`);
export const approvalHistory = (id: string) => get<AuditDto[]>(`/approval/${encodeURIComponent(id)}/history`);

/** 审批动作。⚠ 不再接受 actor 参数——操作人由服务端从登录令牌取（A4）。
 *  客户端自报姓名的审批留痕等于没有留痕。 */
export async function approvalAct(id: string, action: ApprovalAction, note = ''): Promise<ApprovalDto> {
  const res = await fetch(`${BASE}/approval/${encodeURIComponent(id)}/action`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ action, note }),
  });
  if (res.status === 401) { onUnauthorized?.(); throw new Error('未登录或登录已过期'); }
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApprovalRejected(res.status, (body as { detail?: GateDetail | string }).detail ?? `${res.status}`);
  return body as ApprovalDto;
}

export interface KittingDto {
  kitting_pct: number; gate: number; pass: boolean;
  lines: KittingLine[]; purchase_count: number; purchase_amount: number;
}
export const getKitting = () => get<KittingDto>('/supply/kitting');

export interface ConsistencyDto {
  consistency_pct: number; gate: number; pass: boolean;
  reason?: string; assigned?: number; covered?: number;
  by_factory?: Record<string, { run_id: number; jobs: number; status: string }>;
}
export const getConsistency = () => get<ConsistencyDto>('/consistency');

/* ── 指标口径元数据（D-01）──────────────────────────────────────────── */

export interface MetricSpecDto {
  label: string; unit: string; decimals: number; basis?: string;
}
export const metricSpec = () => get<Record<string, MetricSpecDto>>('/metrics/spec');

/* ── 任务谱系（A6，从 solve_run + audit_log 自动串出）──────────────── */

export interface TraceNode {
  at: string; kind: string; title: string; subject: string; actor: string;
  detail: string; ref: string;
  /** trace_id = 同一轮计划周期直接串联；time_window = 按时间窗归入，证据强度较弱 */
  attributed_by: 'trace_id' | 'time_window';
  ok: boolean;
}
export interface TraceChain {
  trace_id: string | null; started_at?: string; ended_at?: string;
  node_count?: number; factories?: string[]; actors?: string[];
  has_failure?: boolean; nodes?: TraceNode[];
  /** trace_id 为 null 时说明为什么没有 */
  reason?: string;
}
export interface TraceSummary {
  trace_id: string; started_at: string; started_by: string;
  solve_runs: number; schedule_runs: number; audit_events: number;
  assigned?: number; fulfill_pct?: number;
}
export const listTraces = () => get<TraceSummary[]>('/trace');
export const getTrace = (id: string) => get<TraceChain>(`/trace/${id}`);

/* ── 外部对接（架构预留，恒为未接通）───────────────────────────────── */

export interface IntegrationItem {
  code: string; name: string; vendor: string; direction: string;
  carries: string; consumers: string[];
  status: string; status_name: string; missing: string[];
  may_display_as_connected: boolean;
}
export interface IntegrationsDto {
  items: IntegrationItem[]; connected: number; total: number; note: string;
}
/** 连接状态由**服务端**给出。前端不得自行渲染成「已接通」——
 *  假状态灯正是这一版要根除的问题。 */
export const listIntegrations = () => get<IntegrationsDto>('/integrations');
